/**
 * Multi-select tag filter shown in list page toolbars.
 *
 * Identical UX to a typical "facet filter" dropdown: trigger shows the
 * count of active filters, the popover renders a checkbox list of
 * in-use tags with per-resource counts, "Clear" resets the selection.
 *
 * AND semantics mirror the backend: passing two tags shows rows that
 * carry both. The trigger string reflects this with a ``∩`` separator.
 */
import * as PopoverPrimitive from "@radix-ui/react-popover";
import { useQuery } from "@tanstack/react-query";
import { Tag as TagIcon, X } from "lucide-react";
import * as React from "react";
import { useTranslation } from "react-i18next";

import { tagsApi } from "@/lib/api-tags";
import { cn } from "@/lib/cn";

export interface TagFilterProps {
  value: string[];
  onChange: (next: string[]) => void;
  /** Restrict the listed tags + counts to this resource. */
  scope?: "workflows" | "ingress_sources" | "canonicals";
  className?: string;
}

export function TagFilter({ value, onChange, scope, className }: TagFilterProps) {
  const { t } = useTranslation();
  const [open, setOpen] = React.useState(false);

  const tagsQ = useQuery({
    queryKey: ["tags-catalog"],
    queryFn: tagsApi.list,
    staleTime: 30_000,
  });

  const items = React.useMemo(() => {
    const all = tagsQ.data?.tags ?? [];
    if (!scope) return all;
    return all.filter((u) => u[scope] > 0);
  }, [tagsQ.data, scope]);

  function toggle(tag: string) {
    if (value.includes(tag)) {
      onChange(value.filter((t) => t !== tag));
    } else {
      onChange([...value, tag]);
    }
  }

  const label = React.useMemo(() => {
    if (value.length === 0) return t("tags.filterAll", "Tags");
    if (value.length === 1) return value[0];
    return value.join(" ∩ ");
  }, [value, t]);

  return (
    <PopoverPrimitive.Root open={open} onOpenChange={setOpen}>
      <PopoverPrimitive.Trigger asChild>
        <button
          type="button"
          className={cn(
            "inline-flex h-8 max-w-[20rem] items-center gap-1.5 truncate rounded-md border border-border bg-surface px-2.5 text-sm",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1",
            value.length > 0 && "border-accent/40 text-foreground",
            className,
          )}
        >
          <TagIcon className="h-3.5 w-3.5 shrink-0 text-muted-foreground" strokeWidth={1.5} />
          <span className="truncate font-mono text-[12px]">{label}</span>
          {value.length > 0 && (
            <span
              role="button"
              aria-label={t("common.cancel")}
              onPointerDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onChange([]);
              }}
              className="ml-0.5 rounded-full p-0.5 text-muted-foreground hover:text-foreground"
            >
              <X className="h-3 w-3" strokeWidth={1.5} />
            </span>
          )}
        </button>
      </PopoverPrimitive.Trigger>

      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Content
          sideOffset={4}
          align="start"
          className={cn(
            "z-50 max-h-72 w-64 overflow-y-auto rounded-md border border-border bg-elevated text-sm shadow-lg",
          )}
        >
          {tagsQ.isLoading && (
            <div className="px-3 py-2 text-xs text-muted-foreground">
              {t("common.loading")}
            </div>
          )}
          {!tagsQ.isLoading && items.length === 0 && (
            <div className="px-3 py-2 text-xs text-muted-foreground">
              {t("tags.noneInUse", "No tags in use yet")}
            </div>
          )}
          <ul role="listbox" className="py-1">
            {items.map((u) => {
              const checked = value.includes(u.tag);
              const count = scope
                ? u[scope]
                : u.workflows + u.ingress_sources + u.canonicals;
              return (
                <li
                  key={u.tag}
                  role="option"
                  aria-selected={checked}
                  onClick={() => toggle(u.tag)}
                  className={cn(
                    "flex cursor-pointer items-center justify-between gap-3 px-3 py-1.5",
                    "hover:bg-muted/70",
                    checked && "bg-accent/10",
                  )}
                >
                  <span className="flex min-w-0 items-center gap-2">
                    <input
                      type="checkbox"
                      checked={checked}
                      readOnly
                      className="h-3 w-3 accent-accent"
                    />
                    <span className="truncate font-mono text-[12px]">{u.tag}</span>
                  </span>
                  <span className="shrink-0 text-[11px] text-muted-foreground">
                    {count}
                  </span>
                </li>
              );
            })}
          </ul>
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  );
}
